/**
 * Iter-334 Phase A — Booking Confirmation / Guest Receipt page.
 * URL: /booking/:id?token=<share_token>[&session_id=<checkout_session_id>]
 *
 * Universal: works for both logged-in clients and guests using the share_token.
 * Shows current status, payments made, and offers a "Pay Balance" CTA when applicable.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Calendar, MapPin, Mail, Phone, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function BookingConfirmation() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token');
  const sessionId = params.get('session_id');

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/marketplace/bookings/${token}`);
      setBooking(r.data);
    } catch {
      toast.error('Unable to load booking');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Poll a couple of times right after Stripe success while webhook lands
  useEffect(() => {
    if (!sessionId || !booking) return;
    if (booking.status === 'paid_in_full' || booking.status === 'deposit_paid') return;
    const t = setTimeout(load, 2500);
    return () => clearTimeout(t);
  }, [sessionId, booking, load]);

  const payBalance = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/marketplace/bookings/${token}/pay-balance`, { origin_url: window.location.origin });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create payment link');
    } finally { setPaying(false); }
  };

  if (!token) return <div className="min-h-[100dvh] flex items-center justify-center text-slate-600">Missing access token.</div>;
  if (loading) return <div className="min-h-[100dvh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  if (!booking) return <div className="min-h-[100dvh] flex items-center justify-center text-slate-600">Booking not found.</div>;

  const v = booking.vendor;
  const accent = v.brand_accent_color || '#0f766e';
  const isDone = booking.status === 'paid_in_full' || booking.status === 'completed';
  const isDeposit = booking.status === 'deposit_paid';
  const remaining = Number(booking.amount_due || 0);

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      {v.cover_url && <div className="h-40 sm:h-56 bg-cover bg-center" style={{ backgroundImage: `url(${v.cover_url})` }} />}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <Card className="p-5">
          <div className="flex items-center gap-3">
            {v.logo_url && <img src={v.logo_url} alt="" className="w-14 h-14 rounded-lg object-cover" />}
            <div>
              <div className="text-xl font-bold" data-testid="booking-vendor-name">{v.company_name}</div>
              <div className="text-xs text-slate-500">Booking receipt · {booking.id.slice(0, 8)}</div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <div
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${isDone ? 'bg-emerald-100 text-emerald-800' : isDeposit ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}
              data-testid="booking-status"
            >
              {isDone && <CheckCircle2 className="w-3.5 h-3.5" />}
              {booking.status.replace(/_/g, ' ').toUpperCase()}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <div>
              <div className="text-slate-500 text-xs uppercase tracking-wide">Event</div>
              <div className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-slate-400" />{new Date(booking.event_start_at).toLocaleString()}</div>
              {booking.event_address?.address && <div className="flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5 text-slate-400" />{booking.event_address.address}</div>}
            </div>
            <div>
              <div className="text-slate-500 text-xs uppercase tracking-wide">Package</div>
              <div className="font-medium">{booking.package?.title || '—'}</div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Total</span><span className="font-medium">{fmt(booking.total_amount)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Paid so far</span><span className="font-medium text-emerald-700" data-testid="amount-paid">{fmt(booking.amount_paid)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Balance due</span><span className="font-semibold" data-testid="amount-due">{fmt(booking.amount_due)}</span></div>
          </div>

          {remaining > 0.01 && booking.package?.allows_partial_payments && (
            <Button onClick={payBalance} disabled={paying} className="w-full mt-4 text-white" style={{ backgroundColor: accent }} data-testid="pay-balance-btn">
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay balance · ${fmt(remaining)}`}
            </Button>
          )}
        </Card>

        {(v.contact_email || v.contact_phone) && (
          <Card className="p-4 text-sm">
            <div className="font-semibold mb-2">Contact {v.company_name}</div>
            {v.contact_email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /><a className="hover:underline" href={`mailto:${v.contact_email}`}>{v.contact_email}</a></div>}
            {v.contact_phone && <div className="flex items-center gap-2 mt-1"><Phone className="w-3.5 h-3.5 text-slate-400" /><a className="hover:underline" href={`tel:${v.contact_phone}`}>{v.contact_phone}</a></div>}
          </Card>
        )}

        {v.cancellation_policy && (
          <Card className="p-4 text-sm">
            <div className="font-semibold mb-1">Cancellation policy</div>
            <div className="text-slate-600 whitespace-pre-wrap">{v.cancellation_policy}</div>
          </Card>
        )}

        <div className="flex justify-between items-center text-xs text-slate-500 pt-2">
          <Link to={`/vendor/${v.slug}`} className="hover:underline inline-flex items-center gap-1">View vendor page <ExternalLink className="w-3 h-3" /></Link>
          <span>Secure payments powered by iEvntür</span>
        </div>
      </div>
    </div>
  );
}
